import { Operator } from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';

export type ADFilterType = 'string' | 'boolean' | 'array' | 'date';
export type ADOperator = Operator;

export interface ADFilterDefinition {
  key: string;
  label: string;
  type: ADFilterType;
  operators: ADOperator[];
  defaultOperator: ADOperator;
  placeholder?: string;
  values?: Array<{ label: string; value: string }> | 'dynamic' | 'userInput';
  multiSelect?: boolean;
}

const CORE_AD_FILTERS: ADFilterDefinition[] = [
  {
    key: 'adObjectType',
    label: 'AD Object Type',
    type: 'array',
    operators: ['is any of', 'is none of'],
    defaultOperator: 'is any of',
    multiSelect: true,
    values: [
      { label: 'AD Domain', value: 'ADDomain' },
      { label: 'AD User', value: 'ADUser' },
      { label: 'AD Group', value: 'ADGroup' },
      { label: 'AD Computer', value: 'ADComputer' },
      { label: 'AD Container', value: 'ADContainer' },
      { label: 'Certificate Template', value: 'ADCertTemplate' },
      { label: 'AIA CA', value: 'ADAIACA' },
      { label: 'AD GPO', value: 'ADGPO' },
      { label: 'AD OU', value: 'ADOU' },
      { label: 'AD Local Group', value: 'ADLocalGroup' },
      { label: 'AD Local User', value: 'ADLocalUser' },
      { label: 'AD Root CA', value: 'ADRootCA' },
      { label: 'AD Enterprise CA', value: 'ADEnterpriseCA' },
      { label: 'AD NTAuth Store', value: 'ADNTAuthStore' },
      { label: 'AD Issuance Policy', value: 'ADIssuancePolicy' },
    ],
  },

  {
    key: 'name',
    label: 'Name',
    type: 'string',
    operators: ['equals', 'not equals', 'contains', 'starts with', 'ends with'],
    defaultOperator: 'contains',
    values: 'userInput',
  },
  {
    key: 'domain',
    label: 'Domain',
    type: 'array',
    operators: [
      'equals',
      'not equals',
      'is any of',
      'is none of',
      'contains',
      'starts with',
    ],
    defaultOperator: 'is any of',
    multiSelect: true,
    values: 'dynamic',
  },
  {
    key: 'samaccountname',
    label: 'SAM Account Name',
    type: 'array',
    operators: [
      'equals',
      'not equals',
      'is any of',
      'is none of',
      'contains',
      'starts with',
    ],
    defaultOperator: 'contains',
    multiSelect: true,
    values: 'dynamic',
  },
  {
    key: 'distinguishedname',
    label: 'Distinguished Name',
    type: 'string',
    operators: ['equals', 'not equals', 'contains', 'starts with', 'ends with'],
    defaultOperator: 'starts with',
  },
  {
    key: 'displayname',
    label: 'Display Name',
    type: 'string',
    operators: ['equals', 'not equals', 'contains', 'starts with'],
    defaultOperator: 'contains',
  },
  {
    key: 'description',
    label: 'Description',
    type: 'string',
    operators: ['equals', 'not equals', 'contains', 'starts with'],
    defaultOperator: 'contains',
  },
  {
    key: 'objectid',
    label: 'Object ID',
    type: 'string',
    operators: ['equals', 'not equals', 'contains', 'starts with', 'ends with'],
    defaultOperator: 'contains',
  },
  {
    key: 'operatingsystem',
    label: 'Operating System',
    type: 'string',
    operators: [
      'equals',
      'not equals',
      'contains',
      'does not contain',
      'starts with',
      'ends with',
    ],
    defaultOperator: 'contains',
    values: 'userInput',
  },

  {
    key: 'status',
    label: 'Status',
    type: 'array',
    operators: ['is any of', 'is none of'],
    defaultOperator: 'is any of',
    multiSelect: true,
    values: [
      { label: 'Active', value: 'A' },
      { label: 'Inactive', value: 'IN' },
      { label: 'Unavailable', value: 'U' },
      { label: 'Expiring', value: 'I' },
      { label: 'Frozen', value: 'F' },
      { label: 'Deleted', value: 'D' },
    ],
  },
  {
    key: 'hasspn',
    label: 'Has SPN',
    type: 'boolean',
    operators: ['equals', 'not equals'],
    defaultOperator: 'equals',
    values: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
];

const AD_BOOLEAN_FIELD_NAMES = [
  'admincount',
  'isaclprotected',
  'pwdneverexpires',
  'isdc',
  'unconstraineddelegation',
  'authenticationenabled',
  'dontreqpreauth',
  'passwordexpired',
  'smartcardrequired',
  'trustedtoauth',
  'sensitive',
  'hasspn',
];

export const AD_NON_RELEVANT_FIELDS = new Set([
  'asname',
  'asnumber',
  'attacksurface',
  'cloud',
  'port',
  'protocol',
  'private',
  'country',
  'registrar',
  'registrant',
  'email',
  'dns',
  'created',
  'visited',
  'source',
]);

function generateBooleanFilters(): ADFilterDefinition[] {
  return AD_BOOLEAN_FIELD_NAMES.map(field => ({
    key: field,
    label: formatFieldLabel(field),
    type: 'boolean' as const,
    operators: ['equals', 'not equals'] as ADOperator[],
    defaultOperator: 'equals' as ADOperator,
    multiSelect: false,
    values: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  }));
}

function formatFieldLabel(field: string): string {
  const labelMap: Record<string, string> = {
    admincount: 'Admin Count',
    isaclprotected: 'ACL Protected',
    pwdneverexpires: 'Password Never Expires',
    isdc: 'Is Domain Controller',
    unconstraineddelegation: 'Unconstrained Delegation',
    authenticationenabled: 'Authentication Enabled',
    dontreqpreauth: 'Pre-Auth Not Required',
    passwordexpired: 'Password Expired',
    smartcardrequired: 'Smart Card Required',
    trustedtoauth: 'Trusted for Authentication',
    sensitive: 'Cannot Be Delegated',
    samaccountname: 'SAM Account Name',
    hasspn: 'Has SPN',
  };

  return (
    labelMap[field] ||
    field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  );
}

export const ALL_AD_FILTERS: ADFilterDefinition[] = [
  ...CORE_AD_FILTERS,
  ...generateBooleanFilters(),
];

export function getADRelevantFilters(): ADFilterDefinition[] {
  const seenFields = new Set<string>();

  return ALL_AD_FILTERS.filter(filter => {
    if (AD_NON_RELEVANT_FIELDS.has(filter.key) || seenFields.has(filter.key)) {
      return false;
    }
    seenFields.add(filter.key);
    return true;
  });
}

export function getFilterByKey(key: string): ADFilterDefinition | undefined {
  return getADRelevantFilters().find(f => f.key === key);
}

export function getCorrectedADFilters(): ADFilterDefinition[] {
  return getADRelevantFilters();
}
